import { useEffect, useMemo, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Layout } from "./components/Layout";
import { CartDrawer } from "./components/CartDrawer";
import Home from "./pages/Home";
import { CategoryPage, ProductPage, ShopPage } from "./pages/ShopPages";
import {
  AboutPage,
  ArtisansPage,
  CollaboratePage,
  ContactPage,
  FounderPage,
  GiftingPage,
  ImpactPage,
  SustainabilityPage,
} from "./pages/StoryPages";
import { defaultContact } from "./data/siteContent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BASE_PATH = process.env.PUBLIC_URL || "";
const API = `${BACKEND_URL || BASE_PATH}/api`;

function App() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [impact, setImpact] = useState({ metrics: [] });
  const [contact, setContact] = useState(defaultContact);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    const loadSite = async () => {
      try {
        const [productRes, categoryRes, impactRes, contactRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/categories`),
          axios.get(`${API}/impact`),
          axios.get(`${API}/contact-config`),
        ]);
        setProducts(productRes.data);
        setCategories(categoryRes.data);
        setImpact(impactRes.data);
        setContact(contactRes.data);
      } catch (error) {
        console.error("Project Umeed site data could not be loaded", error);
      }
    };
    loadSite();
  }, []);

  const cartCount = useMemo(() => cart.reduce((sum, item) => sum + item.quantity, 0), [cart]);

  const addToCart = (product) => {
    setCart((current) => {
      const existing = current.find((item) => item.slug === product.slug);
      if (existing) {
        return current.map((item) => item.slug === product.slug ? { ...item, quantity: item.quantity + 1 } : item);
      }
      return [...current, { ...product, quantity: 1 }];
    });
    setCartOpen(true);
  };

  const updateQuantity = (slug, quantity) => {
    if (quantity < 1) {
      setCart((current) => current.filter((item) => item.slug !== slug));
      return;
    }
    setCart((current) => current.map((item) => item.slug === slug ? { ...item, quantity } : item));
  };

  const removeFromCart = (slug) => setCart((current) => current.filter((item) => item.slug !== slug));
  const clearCart = () => setCart([]);

  const whatsappProductOrder = (product) => {
    const cleanNumber = contact.whatsapp_number.replace(/\D/g, "") || "910000000000";
    const message = encodeURIComponent(`Hello Project Umeed, I would like to order ${product.name} (₹${product.price}). Please share availability, shipping and payment details.`);
    window.open(`https://wa.me/${cleanNumber}?text=${message}`, "_blank", "noopener,noreferrer");
  };

  return (
    <BrowserRouter basename={BASE_PATH || "/"}>
      <Layout cartCount={cartCount} onCartOpen={() => setCartOpen(true)} contact={contact}>
        <Routes>
          <Route path="/" element={<Home products={products} categories={categories} impact={impact} onAddToCart={addToCart} onWhatsAppOrder={whatsappProductOrder} />} />
          <Route path="/shop" element={<ShopPage products={products} categories={categories} onAddToCart={addToCart} onWhatsAppOrder={whatsappProductOrder} />} />
          <Route path="/category/:slug" element={<CategoryPage products={products} categories={categories} onAddToCart={addToCart} onWhatsAppOrder={whatsappProductOrder} />} />
          <Route path="/product/:slug" element={<ProductPage products={products} onAddToCart={addToCart} onWhatsAppOrder={whatsappProductOrder} />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/about-project-umeed" element={<AboutPage />} />
          <Route path="/meet-the-artisans" element={<ArtisansPage />} />
          <Route path="/sustainability" element={<SustainabilityPage />} />
          <Route path="/impact" element={<ImpactPage impact={impact} />} />
          <Route path="/corporate-gifting" element={<GiftingPage />} />
          <Route path="/founders-note" element={<FounderPage />} />
          <Route path="/collaborate" element={<CollaboratePage />} />
          <Route path="/contact" element={<ContactPage contact={contact} />} />
        </Routes>
      </Layout>
      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        cart={cart}
        contact={contact}
        updateQuantity={updateQuantity}
        removeFromCart={removeFromCart}
        clearCart={clearCart}
      />
    </BrowserRouter>
  );
}

export default App;
